/**
 * Unified search query parser supporting Google and Microsoft/SharePoint syntax.
 *
 * AND (all equivalent):
 * - "water permit" (implicit)
 * - "water AND permit"
 * - "water & permit"
 *
 * OR (all equivalent):
 * - "water OR permit"
 * - "water or permit"
 * - "water | permit"
 *
 * NOT/exclusion (all equivalent):
 * - "-expired" (Google-style)
 * - "NOT expired" (Microsoft-style)
 *
 * Quoted phrases:
 * - '"water permit"' or "'water permit'"
 *
 * Mixed examples:
 * - 'water "annual report" -draft OR final'
 * - 'water AND permit NOT expired | revoked'
 */

const DOCUMENT_EXTENSIONS = /\.(?:pdf|xlsx?|docx?|csv|tiff?|jpe?g|png)$/i;

/**
 * Check if a term looks like a filename (has a known document file extension).
 */
export function looksLikeFilename(term: string): boolean {
   return DOCUMENT_EXTENSIONS.test(term);
}

export interface SearchKeywords {
   must: string[];
   must_not: string[];
   should: string[];
   /** Terms that were explicitly quoted by the user */
   quoted: Set<string>;
}

interface Token {
   value: string;
   quoted: boolean;
}

function tokenize(query: string): Token[] {
   const tokens: Token[] = [];
   let current = '';
   let inQuotes = false;
   let quoteChar = '';

   for (const char of query) {
      if ((char === '"' || char === '\'') && !inQuotes) {
         inQuotes = true;
         quoteChar = char;
         continue;
      }

      if (char === quoteChar && inQuotes) {
         if (current) {
            tokens.push({ value: current, quoted: true });
            current = '';
         }
         inQuotes = false;
         quoteChar = '';
         continue;
      }

      if (/\s/.test(char) && !inQuotes) {
         if (current) {
            tokens.push({ value: current, quoted: false });
            current = '';
         }
         continue;
      }

      current += char;
   }

   if (current) {
      tokens.push({ value: current, quoted: false });
   }

   return tokens;
}

function isAndOperator(token: Token): boolean {
   return !token.quoted && (token.value.toUpperCase() === 'AND' || token.value === '&');
}

function isOrOperator(token: Token): boolean {
   return !token.quoted && (token.value.toUpperCase() === 'OR' || token.value === '|');
}

function isNotOperator(token: Token): boolean {
   return !token.quoted && token.value.toUpperCase() === 'NOT';
}

/**
 * Parse a search query string into structured keywords.
 *
 * @example
 * parseUserQuery('water "annual report" -draft OR final')
 * // { must: ['water', 'annual report'], must_not: ['draft'], should: ['final'], quoted: Set{'annual report'} }
 */
export function parseUserQuery(queryString: string): SearchKeywords {
   const result: SearchKeywords = { must: [], must_not: [], should: [], quoted: new Set() };

   if (!queryString?.trim()) {
      return result;
   }

   const tokens = tokenize(queryString);

   function addTerm(arr: string[], token: Token) {
      arr.push(token.value);
      if (token.quoted) {
         result.quoted.add(token.value);
      }
   }

   let i = 0;
   while (i < tokens.length) {
      const token = tokens[i];

      // Skip AND operators (terms are implicitly ANDed)
      if (isAndOperator(token)) {
         i++;
         continue;
      }

      // Check for OR operator
      if (isOrOperator(token)) {
         // Move previous must term to should
         if (result.must.length) {
            result.should.push(result.must.pop()!);
         }
         // Next token goes to should
         i++;
         if (i < tokens.length) {
            let nextToken = tokens[i];
            // Skip AND after OR if present
            if (isAndOperator(nextToken)) {
               i++;
               if (i < tokens.length) {
                  nextToken = tokens[i];
               }
            }
            // Handle NOT/negation after OR
            if (isNotOperator(nextToken)) {
               i++;
               if (i < tokens.length) {
                  addTerm(result.must_not, tokens[i]);
               }
            }
            else if (!nextToken.quoted && nextToken.value.startsWith('-') && nextToken.value.length > 1) {
               result.must_not.push(nextToken.value.slice(1));
            }
            else {
               addTerm(result.should, nextToken);
            }
         }
         i++;
         continue;
      }

      // Check for NOT operator (Microsoft-style)
      if (isNotOperator(token)) {
         i++;
         if (i < tokens.length) {
            const nextToken = tokens[i];
            if (!isAndOperator(nextToken) && !isOrOperator(nextToken) && !isNotOperator(nextToken)) {
               addTerm(result.must_not, nextToken);
            }
         }
         i++;
         continue;
      }

      // Check for negation prefix (Google-style: -term)
      if (!token.quoted && token.value.startsWith('-') && token.value.length > 1) {
         result.must_not.push(token.value.slice(1));
         i++;
         continue;
      }

      // Regular term - add to must
      addTerm(result.must, token);
      i++;
   }

   return result;
}

/**
 * Reconstruct a query string from parsed SearchKeywords.
 * The result, when re-parsed, should yield the same must/should/must_not arrays.
 *
 * The key insight: the parser moves the term immediately before an OR into
 * `should`, so the should terms must appear as `term1 OR term2 OR ...` without
 * a leading OR — otherwise the last must term gets incorrectly absorbed into
 * should on re-parse.
 */
export function reconstructQuery(parsed: SearchKeywords): string {
   const parts: string[] = [];
   const quote = (t: string) => {
      if (parsed.quoted.has(t) || t.includes(' ')) {
         return `"${t}"`;
      }
      return t;
   };

   for (const term of parsed.must) parts.push(quote(term));
   if (parsed.should.length > 0) {
      const shouldParts = parsed.should.map(quote);
      parts.push(shouldParts.join(' OR '));
   }
   for (const term of parsed.must_not) parts.push(`-${quote(term)}`);

   return parts.join(' ');
}

/**
 * Check if a SearchKeywords object has any search terms.
 */
export function hasKeywords(keywords: SearchKeywords | null | undefined): boolean {
   if (!keywords) {
      return false;
   }
   return keywords.must.length > 0 || keywords.must_not.length > 0 || keywords.should.length > 0;
}
